import { PlayerMode } from '../../../../services/course-player.service';

/**
 * KAN-415: classify a BASE course-read failure (GET /courses/{id}) as a clean
 * "you no longer have access" denial for the learner course player.
 *
 * Why: the learner player loads the course via the base read FIRST (before the
 * /modules and /content routes). After a learner leaves the org that owns a
 * course, that org-scoped read returns 404 COURSE_NOT_FOUND (getCourseById
 * throws "Course not found") or 403 COURSE_ACCESS_DENIED. Both cases mean the
 * learner cannot access the course and a retry can never fix it, so the modal
 * must render a graceful access-denied state instead of the generic, retryable
 * "Error Loading Course" crash.
 *
 * Scope guards:
 * - Only the `learner` player gets this treatment. An instructor previewing a
 *   genuinely missing course should still see the ordinary error.
 * - The paid-entitlement CTA codes (COURSE_ACCESS_REQUIRED / COURSE_ACCESS_EXPIRED,
 *   KAN-353) are deliberately NOT denials — they fire on the STRUCTURE load and
 *   drive the Buy/Renew CTA, which this must not regress. The base read returns
 *   200 for a paid-but-unentitled member, so those codes never reach here anyway.
 */
export function isLearnerCourseAccessDenial(error: any, playerMode: PlayerMode): boolean {
  if (playerMode !== 'learner') return false;

  const status = error?.status;
  const code = error?.error?.error?.code ?? error?.error?.code;

  // 403 from the base read means access is revoked (departed org member).
  if (status === 403 && (code === 'COURSE_ACCESS_DENIED' || code === 'NO_COURSE_ACCESS')) {
    return true;
  }

  // 404 on the org-scoped base learner read means the course is not visible to
  // this learner (left org, or the course was removed). Any 404 here qualifies —
  // the endpoint only 404s as COURSE_NOT_FOUND — and a retry cannot recover it.
  if (status === 404) {
    return true;
  }

  return false;
}
